import React, { useEffect, useState } from "react";
import { POST_REQUEST } from "@/utils/fetch";
import { PrimaryBtn, SecondaryBtn } from "../elements/Buttons";
import { useSession } from "next-auth/react";
import moment from "moment/moment";
import "moment/locale/fr";
import "moment/locale/ar";

import {
  Product,
  ProductColor,
  SpecialProductData,
  UserSession,
} from "@/utils/types";
import { useDrawer } from "@/utils/DrawerProvider";
import { UploadSpot } from "../elements/FileUpload";
import { useRouter } from "next/navigation";
import { NumberPicker } from "../elements/NumberPicker";
import { toast } from "react-toastify";

import { useDialog } from "@/utils/DialogProvider";
import { useTranslation } from "react-i18next";
import { apiBaseUrl } from "next-auth/client/_utils";
import api from "@/app/lib/api";
import { getSocket } from "@/utils/sharedUtils";
import AutocompleteComponent from "../elements/AutocompleteComponent";

export default function AddSpecialOrderDrawer({
  drawerid,
  user_id,
  handleError,
  handleSuccess,
}: {
  drawerid?: string;
  user_id?: number;
  handleError: Function;
  handleSuccess: Function;
}) {
  const socket = getSocket();

  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language as "ar" | "fr" | "en";
  moment.locale(currentLocale);

  const router = useRouter();
  const session = useSession().data as UserSession;
  const { closeDrawer, openDrawer } = useDrawer();

  const [isToAmazon, setIsToAmazon] = useState<"oui" | "non">("non");
  const [amazonAddress, setAmazonAddress] = useState("");
  const [wilaya, setWilaya] = useState("");
  const [emptyFieldsErr, setEmptyFieldsErr] = useState(false);
  const [productData, setProductData] = useState<
    Omit<SpecialProductData, "addedOrder" | "history">
  >({
    name: "",
    url: "",
    is_public: 0,
    images: [" "],
    added_by: "user",
    user_id: user_id ?? session?.id!,
    package_info: { width: 0, height: 0, length: 0, weight: 0, quantity: 0 },
    packages_number: 0,
    price: 0,
  });

  const [orderData, setOrderData] = useState({
    comment: "",
    quantity: 0,
    shipping_adress: session?.adress,
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setProductData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if((productData?.packages_number??0)* productData?.package_info?.height <5){
      toast.error("Le poids minimal pour une commande spéciale est de 5kg");
      return { error: true };
    }

    try {
      const addedProduct = await api.products.insert({
        ...productData,
        price: productData.price,
        weight: productData.package_info.weight,
        name: {
          ar: productData.name,
          fr: productData.name,
          en: productData.name,
        },
        images: productData.images ? JSON.stringify(productData.images) : "[]",
      });
      const addedOrder = {
        ...orderData,
        shipping_adress: ` ${orderData.shipping_adress} -  ${wilaya} `,
        amazon_address: isToAmazon === "oui" ? amazonAddress : null,
        user_id: user_id ?? session?.id!,
        is_special: 1 as 0 | 1,
        status: 1,
        product_id: addedProduct?.id,
        size: `${6}`,
      };

      await api.orders.insert(
        {
          ...productData,
          addedOrder: addedOrder,
          history: {
            user_id: session?.id!,
            action: `Création de la commande spéciale`,
            color: "#296760",
          },
        },
        session
      );

      if (socket) {
        socket.emit("send_msg", { roomId: ["admin"], new: "orders" });
        socket.disconnect();
      }
      toast.success(t("orderSuccessfullyLaunched"), { position: "top-center" });
      handleSuccess();
      closeDrawer(drawerid ?? "");
      if (session?.role == "user") {
        setTimeout(() => {
          router.push("/my_space?tab=specialOrders");
        }, 1200);
      }
      return { success: true };
    } catch (error: any) {
      toast.error(`${t("error")} : ${error?.message}`, {
        position: "top-center",
      });
      console.error("Error in catch block:", error);
      return { error: true };
    }
  };

  return (
    <div>
      <div className="drawer-content">
        
        <div>
          <div>
            <label htmlFor="image">{t("productImage")} </label>
            <UploadSpot
              value={" "}
              onChange={(newFile) => {
                setProductData({
                  ...productData,
                  images: [newFile?.url || ""],
                });
              }}
            />
            {emptyFieldsErr &&
              (!productData.images || !productData?.images?.[0]?.trim()) && (
                <p className="f13 red"> {t("requiredField")}</p>
              )}{" "}
          </div>

          <div>
            <label htmlFor="name">{t("productName")} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={productData.name}
              onChange={handleInputChange}
              required
            />
            {emptyFieldsErr && productData.name == "" && (
              <p className="f13 red"> {t("requiredField")}</p>
            )}
          </div>

          <div>
            <label htmlFor="url">{t("productLink")} *</label>
            <textarea
              id="url"
              name="url"
              value={productData.url}
              onChange={handleInputChange}
            />
            {emptyFieldsErr && productData.url == "" && (
              <p className="f13 red"> {t("requiredField")}</p>
            )}
          </div>

          <div className="grid gap5">
            <label htmlFor="size">{t("packageSizeInCm")} *</label>
            <div className="c1-auto mc1 gap5">
              <div className="c3 gap5">
                <div>
                  <label htmlFor="size">{t("height")} *</label>
                  <input
                    placeholder="2cm"
                    type="number"
                    min={0}
                    id="package_info_height"
                    name="package_info_height"
                    value={productData.package_info.height}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        package_info: {
                          ...productData.package_info,
                          height: e.target.value as unknown as number,
                        },
                      })
                    }
                  />
                  {emptyFieldsErr && productData.package_info.height == 0 && (
                    <p className="f13 red"> {t("requiredField")}</p>
                  )}
                </div>{" "}
                <div>
                  <label htmlFor="size">{t("width")} *</label>
                  <input
                    placeholder="2cm"
                    type="number"
                    min={0}
                    id="package_info_width"
                    name="package_info_width"
                    value={productData.package_info.width}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        package_info: {
                          ...productData.package_info,
                          width: e.target.value as unknown as number,
                        },
                      })
                    }
                  />
                  {emptyFieldsErr && productData.package_info.width == 0 && (
                    <p className="f13 red"> {t("requiredField")}</p>
                  )}
                </div>{" "}
                <div>
                  <label htmlFor="size">{t("length")} *</label>
                  <input
                    placeholder="2cm"
                    type="number"
                    min={0}
                    id="package_info_length"
                    name="package_info_length"
                    value={productData.package_info.length}
                    onChange={(e) =>
                      setProductData({
                        ...productData,
                        package_info: {
                          ...productData.package_info,
                          length: e.target.value as unknown as number,
                        },
                      })
                    }
                  />
                  {emptyFieldsErr && productData.package_info.length == 0 && (
                    <p className="f13 red"> {t("requiredField")}</p>
                  )}{" "}
                </div>{" "}
              </div>
              <div>
                <label htmlFor="size">{t("packageQuantity")} *</label>
                <input
                  placeholder="2cm"
                  type="number"
                  min={0}
                  id="package_info_qantity"
                  name="package_info_qantity"
                  value={productData.package_info.quantity}
                  onChange={(e) =>
                    setProductData({
                      ...productData,
                      package_info: {
                        ...productData.package_info,
                        quantity: parseInt(e.target.value),
                      },
                    })
                  }
                />
                {emptyFieldsErr && productData.package_info.quantity == 0 && (
                  <p className="f13 red"> {t("requiredField")}</p>
                )}
              </div>{" "}
            </div>
          </div>

          <div className="c2 gap10 mc1">
            <div>
              <label htmlFor="poids">{t("Packages number")}*</label>
              <input
                placeholder="12"
                type="number"
                min={0}
                id="packages_number"
                name="packages_number"
                value={productData.packages_number}
                onChange={(e) =>
                  setProductData({
                    ...productData,
                    packages_number: parseInt(e.target.value),
                  })
                }
              />
              {emptyFieldsErr && productData.packages_number == 0 && (
                <p className="f13 red"> {t("requiredField")}</p>
              )}
            </div>

            <div>
              <label htmlFor="prix">{t("weightInKg")} *</label>
              <input
                id="price"
                placeholder="1Kg"
                type="number"
                min={0}
                name="price"
                value={productData.package_info.weight}
                onChange={(e) => {
                  const newProductData = { ...productData };
                  newProductData.package_info.weight = e.target
                    .value as unknown as number;

                  setProductData(newProductData);
                }}
              />
              {emptyFieldsErr && productData.package_info.weight == 0 && (
                <p className="f13 red"> {t("requiredField")}</p>
              )}
            </div>
          </div>
          <div className="">
            <label htmlFor=""> {t("wilaya")} *</label>
            <AutocompleteComponent
              is_multiple={false}
              itemType="wilayas"
              onChange={(value: {
                name: { en: string; fr: string; ar: string };
              }) => {
                setWilaya(value?.name?.fr);
              }}
            />
          </div>

          <div className="">
            <label htmlFor="Adresse de Livraison">
              {t("shippingAddress")} *
            </label>

            <input
              id="adress"
              name="adress"
              value={orderData.shipping_adress}
              onChange={(e) => {
                setOrderData({ ...orderData, shipping_adress: e.target.value });
              }}
            />
            {emptyFieldsErr && orderData.shipping_adress == "" && (
              <p className="f13 red"> {t("requiredField")}</p>
            )}
          </div>

          <div className="c2 vcenter gap10">
            <div>
              <label htmlFor="prix">{t("productPrice")} *</label>
              <input
                id="price"
                type="number"
                min={0}
                name="price"
                value={productData.price}
                onChange={(e) => {
                  setProductData({
                    ...productData,
                    price: e.target.value as unknown as number,
                  });
                }}
              />
            </div>
            <div>
              <label htmlFor="prix">{t("currency")} *</label>
              <AutocompleteComponent
                defaultValue={{
                  id: productData?.currency ?? "DZD",
                  name: productData?.currency ?? "DZD",
                }}
                is_multiple={false}
                onChange={(newItem: any) =>
                  setProductData({ ...productData, currency: newItem.name })
                }
                itemType="currencies"
              />
            </div>
            {emptyFieldsErr && productData.price == 0 && (
              <p className="f13 red"> {t("requiredField")}</p>
            )}
          </div>

          <div>
            <label> {t("totalQuantity")} *</label>
            <input
              id="quantity"
              type="number"
              min={0}
              name="quantity"
              value={orderData.quantity}
              onChange={(e) => {
                setOrderData({
                  ...orderData,
                  quantity: parseInt(e.target.value),
                });
              }}
            />

            {emptyFieldsErr && orderData.quantity == 0 && (
              <p className="f13 red"> {t("requiredField")}</p>
            )}
          </div>

          <div>
            <label>{t("Adresse de départ pour l’expédition")}</label>
            <input
              type="text"
              value={t("China")}
              readOnly
              className="readonly"
            />
          </div>

          <div className="auto-c1 gap10 mc1">
            <label>{t("Cette marchandise va-t-elle à Amazon ?")}</label>
            <div className="flex gap30">
              <div>
                <label className="auto-c1 gap10 vcenter">
                  <input
                    type="radio"
                    value="non"
                    className="relative"
                    checked={isToAmazon === "non"}
                    onChange={() => setIsToAmazon("non")}
                  />
                  {t("Non")}
                </label>
              </div>
              <div>
                <label className="auto-c1 gap10 vcenter">
                  <input
                    type="radio"
                    className="relative"
                    value="oui"
                    checked={isToAmazon === "oui"}
                    onChange={() => setIsToAmazon("oui")}
                  />
                  {t("Oui")}
                </label>
              </div>
            </div>
          </div>

          {isToAmazon === "oui" && (
            <div>
              <label>{t("Adresse exacte de l'envoi (Amazon)")}*</label>
              <input
                type="text"
                value={amazonAddress}
                onChange={(e) => setAmazonAddress(e.target.value)}
              />
              {emptyFieldsErr && amazonAddress.trim() === "" && (
                <p className="f13 red"> {t("requiredField")}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="comment"> {t("comment")}</label>
            <textarea
              placeholder=""
              id="comment"
              name="comment"
              value={orderData.comment}
              onChange={(e) => {
                setOrderData({ ...orderData, comment: e.target.value });
              }}
            />
          </div>
        </div>
      </div>
      <div className="drawer-footer">
        <SecondaryBtn
          text={t("cancel")}
          submit={() => closeDrawer(drawerid ?? "")}
        />
        <PrimaryBtn
          submit={() => {
            if (session?.role == "super-admin" || session?.role == "admin") {
              toast.error(
                "Vous ne pouvez pas passer de commande en tant qu'administrateur"
              );
              return { error: true };
            }
            if (
              !productData.name ||
              !productData.url ||
              !productData.package_info.height ||
              !productData.package_info.width ||
              !productData.package_info.length ||
              !productData.package_info.quantity ||
              !productData.price ||
              !productData.package_info.weight ||
              !productData.packages_number ||
              !orderData.quantity ||
              !orderData.shipping_adress ||
              (isToAmazon === "oui" && !amazonAddress) ||
              !productData.images ||
              !productData?.images?.[0]?.trim()
            ) {
              toast.error(t("fillRequiredFields"), {
                position: "top-right",
              });

              setEmptyFieldsErr(true);
              return { error: true };
            }
            openDrawer({
              title: t("getQuote"),
              content: (
                <ConfirmspecialOrderDrawer
                  handleSubmit={handleSubmit}
                ></ConfirmspecialOrderDrawer>
              ),
            });
          }}
          text={t("order")}
        />
      </div>
    </div>
  );
}

const ConfirmspecialOrderDrawer = ({
  drawerid,
  handleSubmit,
}: {
  drawerid?: string;
  handleSubmit: () => any;
}) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawer();
  const { openDialog } = useDialog();

  const Submit = async () => {
    try {
      const response = await handleSubmit();
      if (response.success == true) {
        openDialog("Commande enregistré", <AddConfirm />);
        closeDrawer(drawerid ?? "");
      }
    } catch (error: any) {
      toast.error(`${t("error")} : ${error?.message}`, {
        position: "top-center",
      });
      console.error("Error in catch block:", error);
    }
  };
  return (
    <>
      <div className="dialog-body">
        <p>{t("orderConfirmation")} </p>
        <div className="grid gap10 light-grey-bg br20 p20  ">
          <h4 className="light-blue">
            {t("PS: Les marchandises dangereuses et interdites d'expédition.")}
          </h4>
          <div className="flex gap10" style={{ flexWrap: "wrap" }}>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t(
                "Liquides et des substances contenant une solution à base dalcool."
              )}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Les médicaments et les drogues.")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t(
                "Les métaux précieux tels que l'or, les diamants et les pièces de monnaie."
              )}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t(
                "Matériel répugnant ou dégoûtant, pornographie et/ou matériel obscène et tout article indécent non sollicité ou représentation de quelque nature que ce soit. armes blanches et objets tranchants."
              )}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Armes")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Drones ou Phantom")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Aliments")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Détecteur de métaux")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Pesticides et engrais")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("ilules et médicaments cosmétiques")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Équipement médical et chirurgical ou de haute technologie.")}
            </div>
            <div className="br10 pl20 pr20 p10 grey-bg br-light-blue f13">
              {t("Talkie-walkie")}
            </div>
          </div>
        </div>
      </div>
      <div className="dialog-footer">
        <div className="c2 gap5">
          <SecondaryBtn
            text={t("cancel")}
            submit={() => closeDrawer(drawerid ?? "")}
          />
          <PrimaryBtn text={t("confirm")} submit={Submit} />
        </div>
      </div>
    </>
  );
};

export const AddConfirm = ({ drawerid }: { drawerid?: "" }) => {
  const { t } = useTranslation();
  const { closeDialog } = useDialog();
  const { closeDrawer } = useDrawer();
  return (
    <div className="">
      <div className="dialog-body">
        <p>
          {" "}
          {t(
            "Votre commande à été enregistrée, un devis vous sera communiqué par notre équipe dans les 48h"
          )}{" "}
        </p>
        <div className="dialog-footer space-between">
          <div></div>
          <div>
            {" "}
            <PrimaryBtn
              submit={() => {
                closeDialog();
                closeDrawer(drawerid ?? "");
              }}
              text={t("ok")}
            />
          </div>{" "}
        </div>
      </div>
    </div>
  );
};
